/**
 * Firestore-based role management
 * Collections:
 * - roles/{userId} - User role documents
 * - moderatorActivityLogs/{logId} - Moderator activity audit trail
 * - adminMessages/{messageId} - Admin-to-moderator messaging
 */

import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { db } from './firestore';
import { AppRole, UserRole, ModeratorActivityLog, AdminMessage, ModeratorActionType } from '@/types/roles';
import { auth } from './firebase';

// Hardcoded admin emails for fallback
const ADMIN_EMAILS = ['madhavgargjan20@gmail.com'];

// ====================== ROLE MANAGEMENT ======================

/**
 * Get user role from Firestore
 */
export async function getUserRole(userId: string, email: string): Promise<{
  role: AppRole | null;
  enabled: boolean;
  userRole: UserRole | null;
}> {
  try {
    // Check if email is hardcoded admin
    const isHardcodedAdmin = ADMIN_EMAILS.includes(email.toLowerCase());

    const roleRef = doc(db, 'roles', userId);
    const roleSnap = await getDoc(roleRef);

    if (roleSnap.exists()) {
      const data = roleSnap.data() as Omit<UserRole, 'id'>;
      return {
        role: data.role,
        enabled: data.enabled,
        userRole: { id: roleSnap.id, ...data },
      };
    }

    // If hardcoded admin and no role in DB, treat as admin
    if (isHardcodedAdmin) {
      return {
        role: 'admin',
        enabled: true,
        userRole: {
          id: 'hardcoded',
          user_id: userId,
          email: email,
          role: 'admin',
          enabled: true,
          assigned_by: 'system',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      };
    }

    return { role: null, enabled: false, userRole: null };
  } catch (error) {
    console.error('Error fetching role:', error);
    
    // Fallback to hardcoded admin check
    if (ADMIN_EMAILS.includes(email.toLowerCase())) {
      return {
        role: 'admin',
        enabled: true,
        userRole: {
          id: 'hardcoded',
          user_id: userId,
          email: email,
          role: 'admin',
          enabled: true,
          assigned_by: 'system',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      };
    }
    
    return { role: null, enabled: false, userRole: null };
  }
}

/**
 * Assign a role to a user
 */
export async function assignRole(
  adminEmail: string,
  userId: string,
  userEmail: string,
  role: AppRole
): Promise<{ success: boolean; error?: string }> {
  // Verify admin
  if (!ADMIN_EMAILS.includes(adminEmail.toLowerCase())) {
    return { success: false, error: 'Unauthorized - Admin access required' };
  }

  try {
    const roleRef = doc(db, 'roles', userId);
    const now = new Date().toISOString();

    await setDoc(roleRef, {
      user_id: userId,
      email: userEmail,
      role: role,
      enabled: true,
      assigned_by: adminEmail,
      created_at: now,
      updated_at: now,
    });

    console.log(`Admin ${adminEmail} assigned ${role} role to ${userEmail}`);
    return { success: true };
  } catch (error) {
    console.error('Error assigning role:', error);
    return { success: false, error: 'Failed to assign role' };
  }
}

/**
 * Remove a role from a user
 */
export async function removeRole(
  adminEmail: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  if (!ADMIN_EMAILS.includes(adminEmail.toLowerCase())) {
    return { success: false, error: 'Unauthorized - Admin access required' };
  }

  try {
    const roleRef = doc(db, 'roles', userId);
    await deleteDoc(roleRef);

    console.log(`Admin ${adminEmail} removed role from user ${userId}`);
    return { success: true };
  } catch (error) {
    console.error('Error removing role:', error);
    return { success: false, error: 'Failed to remove role' };
  }
}

/**
 * Toggle role enabled status
 */
export async function toggleRoleEnabled(
  adminEmail: string,
  userId: string,
  enabled: boolean
): Promise<{ success: boolean; error?: string }> {
  if (!ADMIN_EMAILS.includes(adminEmail.toLowerCase())) {
    return { success: false, error: 'Unauthorized - Admin access required' };
  }

  try {
    const roleRef = doc(db, 'roles', userId);
    await updateDoc(roleRef, {
      enabled,
      updated_at: new Date().toISOString(),
    });

    console.log(`Admin ${adminEmail} ${enabled ? 'enabled' : 'disabled'} role for user ${userId}`);
    return { success: true };
  } catch (error) {
    console.error('Error toggling role:', error);
    return { success: false, error: 'Failed to toggle role status' };
  }
}

/**
 * List all moderators
 */
export async function listModerators(): Promise<UserRole[]> {
  try {
    const rolesRef = collection(db, 'roles');
    const q = query(
      rolesRef,
      where('role', '==', 'moderator'),
      orderBy('created_at', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as UserRole[];
  } catch (error) {
    console.error('Failed to fetch moderators:', error);
    return [];
  }
}

// ====================== MODERATOR ACTIVITY LOGS ======================

/**
 * Log moderator activity
 */
export async function logModeratorActivity(
  moderatorId: string,
  moderatorEmail: string,
  actionType: ModeratorActionType,
  actionDescription: string,
  options?: {
    affectedData?: Record<string, unknown>;
    affectedUserId?: string;
    revertible?: boolean;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const logsRef = collection(db, 'moderatorActivityLogs');
    await addDoc(logsRef, {
      moderator_id: moderatorId,
      moderator_email: moderatorEmail,
      action: actionDescription,
      action_type: actionType,
      affected_data: options?.affectedData ?? null,
      affected_user_id: options?.affectedUserId ?? null,
      revertible: options?.revertible ?? false,
      reverted: false,
      reverted_at: null,
      reverted_by: null,
      created_at: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Failed to log moderator activity:', error);
    return { success: false, error: 'Failed to log activity' };
  }
}

/**
 * List moderator activity logs (admin only)
 */
export async function listModeratorActivityLogs(
  adminEmail: string,
  limitCount: number = 100
): Promise<ModeratorActivityLog[]> {
  if (!ADMIN_EMAILS.includes(adminEmail.toLowerCase())) {
    console.error('Unauthorized access to activity logs');
    return [];
  }

  try {
    const logsRef = collection(db, 'moderatorActivityLogs');
    const q = query(
      logsRef,
      orderBy('created_at', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as ModeratorActivityLog[];
  } catch (error) {
    console.error('Failed to fetch activity logs:', error);
    return [];
  }
}

/**
 * Revert a moderator action (mark as reverted)
 */
export async function revertModeratorAction(
  adminEmail: string,
  logId: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  if (!ADMIN_EMAILS.includes(adminEmail.toLowerCase())) {
    return { success: false, error: 'Unauthorized - Admin access required' };
  }

  try {
    const logRef = doc(db, 'moderatorActivityLogs', logId);
    const logSnap = await getDoc(logRef);

    if (!logSnap.exists()) {
      return { success: false, error: 'Log not found' };
    }

    const logData = logSnap.data();
    if (logData.reverted) {
      return { success: false, error: 'Action already reverted' };
    }

    if (!logData.revertible) {
      return { success: false, error: 'This action cannot be reverted' };
    }

    await updateDoc(logRef, {
      reverted: true,
      reverted_at: new Date().toISOString(),
      reverted_by: adminEmail,
    });

    return { success: true, message: 'Action marked as reverted' };
  } catch (error) {
    console.error('Failed to revert action:', error);
    return { success: false, error: 'Failed to revert action' };
  }
}

// ====================== ADMIN MESSAGES ======================

/**
 * Send admin message to moderator(s)
 */
export async function sendAdminMessage(
  senderId: string,
  senderEmail: string,
  subject: string,
  content: string,
  options?: {
    recipientId?: string;
    recipientEmail?: string;
    isBroadcast?: boolean;
  }
): Promise<{ success: boolean; error?: string }> {
  if (!ADMIN_EMAILS.includes(senderEmail.toLowerCase())) {
    return { success: false, error: 'Unauthorized - Admin access required' };
  }

  try {
    const messagesRef = collection(db, 'adminMessages');
    await addDoc(messagesRef, {
      sender_id: senderId,
      sender_email: senderEmail,
      recipient_id: options?.recipientId ?? null,
      recipient_email: options?.recipientEmail ?? null,
      is_broadcast: options?.isBroadcast ?? false,
      subject,
      content,
      read: false,
      read_at: null,
      created_at: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Failed to send admin message:', error);
    return { success: false, error: 'Failed to send message' };
  }
}

/**
 * Get messages for a moderator
 */
export async function getModeratorMessages(
  moderatorId: string
): Promise<AdminMessage[]> {
  try {
    const messagesRef = collection(db, 'adminMessages');
    
    // Get direct messages and broadcasts
    const directQuery = query(
      messagesRef,
      where('recipient_id', '==', moderatorId),
      orderBy('created_at', 'desc'),
      limit(50)
    );

    const broadcastQuery = query(
      messagesRef,
      where('is_broadcast', '==', true),
      orderBy('created_at', 'desc'),
      limit(50)
    );

    const [directSnap, broadcastSnap] = await Promise.all([
      getDocs(directQuery),
      getDocs(broadcastQuery),
    ]);

    const allMessages: AdminMessage[] = [];
    const seenIds = new Set<string>();

    [...directSnap.docs, ...broadcastSnap.docs].forEach((doc) => {
      if (!seenIds.has(doc.id)) {
        seenIds.add(doc.id);
        allMessages.push({ id: doc.id, ...doc.data() } as AdminMessage);
      }
    });

    // Sort by created_at descending
    allMessages.sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return allMessages.slice(0, 50);
  } catch (error) {
    console.error('Failed to fetch moderator messages:', error);
    return [];
  }
}

/**
 * Mark message as read
 */
export async function markMessageRead(
  messageId: string,
  moderatorId: string
): Promise<{ success: boolean }> {
  try {
    const messageRef = doc(db, 'adminMessages', messageId);
    const messageSnap = await getDoc(messageRef);

    if (!messageSnap.exists()) {
      return { success: false };
    }

    const data = messageSnap.data();
    // Only allow marking if recipient or broadcast
    if (data.recipient_id !== moderatorId && !data.is_broadcast) {
      return { success: false };
    }

    await updateDoc(messageRef, {
      read: true,
      read_at: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Failed to mark message as read:', error);
    return { success: false };
  }
}
